/**
 * Legacy Panel Key Migration
 *
 * Applications arrive with their own panel persistence vocabulary
 * (`cizgi_dock_layout`, `hat.*`, `yol.panels`, …). When a registry boots
 * with a namespace for the first time it can pull those legacy keys into the
 * package's namespaced keys, so a user's panel layout survives the adoption
 * release. The mapping runs once: a `done` marker key records completion and
 * legacy entries are removed afterwards (purge-on-migrate keeps stale groups
 * from resurrecting later).
 */

import { panelStorageKey } from './namespace';

const MIGRATION_DONE_SUFFIX = '.panels-migration-done';

/**
 * Copy legacy keys into namespaced ones. `map` is `{ legacyKey: baseKey }`;
 * each value lands at `panelStorageKey(baseKey)`. Only runs when the target
 * key is absent, so a fresh install never overwrites newer state with legacy
 * layout. Safe to call from module scope.
 */
export function migratePanelStorageKeys(map: Record<string, string>): void {
    if (typeof window === 'undefined' || !window.localStorage) return;
    try {
        const doneKey = `${panelStorageKey('')}${MIGRATION_DONE_SUFFIX}`;
        if (localStorage.getItem(doneKey)) return;

        let migrated = false;
        for (const [legacyKey, baseKey] of Object.entries(map)) {
            const targetKey = panelStorageKey(baseKey);
            if (localStorage.getItem(targetKey) !== null) continue;
            const raw = localStorage.getItem(legacyKey);
            if (raw === null) continue;
            localStorage.setItem(targetKey, raw);
            localStorage.removeItem(legacyKey);
            migrated = true;
        }
        localStorage.setItem(doneKey, migrated ? 'migrated' : 'clean');
    } catch {
        // Storage unavailable (quota/private mode): skip silently, defaults win
    }
}

/** Remove every persisted panel key for the current namespace — used by
 * "reset workspace" flows that promise a factory-fresh layout. */
export function purgePanelStorage(bases: string[]): void {
    if (typeof window === 'undefined' || !window.localStorage) return;
    try {
        bases.forEach((base) => localStorage.removeItem(panelStorageKey(base)));
    } catch {
        // Storage unavailable: nothing to purge
    }
}
